// Duenner Client fuer das Plant-Maintenance-Backend unter /api/pm.
// Portierung aus dem Vite-Prototyp (frontend/src/api.js): dort sprach ein eigener
// fetch-Wrapper BASE='/api/pm' an. Im Monolithen gilt dieselbe Auth-Konvention wie
// core/api/apiService (Bearer-JWT aus user.token, mock-Token wird uebersprungen)
// und dasselbe /api-Praefix.
//
// MONOLITH-KONVENTION: `user` ist das ERSTE Argument jeder Funktion und traegt
// den JWT (.token). Die Schreib-Operationen behalten den Idempotency-Key-Header
// aus dem Prototyp — ein Retry darf in SAP niemals ein Duplikat erzeugen.
//
// ALLE Aufrufe (auch Reads) laufen ueber pmCall statt core apiCall: einheitliche
// Fehlerbehandlung (Business-Failures als Rueckgabewert, geworfene Fehler tragen
// .status — OrderDetail unterscheidet damit 404 von "Backend nicht erreichbar")
// und optionale Zusatz-Header (Idempotency-Key, X-PM-Role).

/**
 * Interner Wrapper fuer Aufrufe MIT Zusatz-Headern (z. B. Idempotency-Key).
 * Verhaelt sich wie core apiCall() (/api-Praefix, JSON, Bearer-Auth nur wenn user.token existiert
 * und nicht mit 'mock-' beginnt) — mit EINEM bewussten Unterschied bei Fehlern:
 *   - Traegt der Fehler-Body die WriteResult-Form {ok:false, error} (Business-Failure des Adapters
 *     bzw. 403 des Funktionsrechte-Gates), wird der Body ZURUECKGEGEBEN, nicht geworfen. Die
 *     Screens verzweigen ohnehin auf res.ok und zeigen res.error — so sieht der Nutzer die echte
 *     SAP-/Backend-Meldung statt "HTTP error! status: 400".
 *   - Alles andere (500, Netzwerk, Nicht-JSON) wirft weiterhin, mit Backend-Message falls vorhanden.
 * @param {string} endpoint — Pfad NACH /api, z. B. '/pm/orders'
 * @param {string} method
 * @param {any} data — JSON-Body oder null
 * @param {Object} user — { token, ... }
 * @param {Object} [extraHeaders] — z. B. { 'Idempotency-Key': '...', 'X-PM-Role': 'mechanic' }
 */
const pmCall = async (endpoint, method = 'GET', data = null, user = null, extraHeaders = {}) => {
  const config = {
    method,
    headers: { 'Content-Type': 'application/json', ...extraHeaders },
  };
  if (user?.token && !user.token.startsWith('mock-')) {
    config.headers['Authorization'] = `Bearer ${user.token}`;
  }
  if (data) {
    config.body = JSON.stringify(data);
  }
  const response = await fetch(`/api${endpoint}`, config);
  if (!response.ok) {
    let body = null;
    try { body = await response.json(); } catch { /* kein JSON-Body -> generischer Fehler */ }
    if (body && body.ok === false) return body; // WriteResult-Business-Failure: Screens zeigen body.error
    const err = new Error(body?.error || body?.message || `HTTP error! status: ${response.status}`);
    err.status = response.status; // Screens unterscheiden 404 (fachlich) von Transport-/Serverfehlern
    throw err;
  }
  return response.json();
};

/** Aktive PM-Rolle als Header — auch fuer READS: das serverseitige Werks-Scope-Gate
 *  (routes.js plantScopeError/scopeRows) muss gegen DASSELBE Profil pruefen wie die UI-Rolle,
 *  sonst divergieren Reads (JWT-Rolle) und Writes (Umschalter-Rolle), sobald Profile
 *  unterschiedliche Werke tragen. */
const roleHeader = (role) => (role ? { 'X-PM-Role': role } : {});

/** Schreib-Header buendeln: Idempotency-Key + (falls bekannt) die aktive PM-Rolle fuer das
 *  serverseitige Funktionsrechte-Gate (routes.js requireRight). */
const writeHeaders = (idempotencyKey, role) => ({
  'Idempotency-Key': idempotencyKey,
  ...roleHeader(role),
});

// ==================== PLANT-MAINTENANCE-MODUL (/api/pm/*) ====================

/**
 * GET /pm/me?role=
 * Liefert das Demo-Profil/die Rechte fuer die gewaehlte Rolle.
 * @param {Object} user
 * @param {string} role
 */
export const me = async (user, role) =>
  pmCall(`/pm/me?role=${encodeURIComponent(role)}`, 'GET', null, user);

/**
 * GET /pm/equipment?q=
 * Equipment-/Technisches-Objekt-Suche.
 * @param {Object} user
 * @param {string} q — Suchbegriff
 */
export const searchEquipment = async (user, q, role) =>
  pmCall(`/pm/equipment?q=${encodeURIComponent(q)}`, 'GET', null, user, roleHeader(role));

/**
 * GET /pm/equipment/:id/notifications
 * Fruehere Meldungen an einem Equipment.
 * @param {Object} user
 * @param {string} id — Equipment-ID
 */
export const equipmentNotifications = async (user, id, role) =>
  pmCall(`/pm/equipment/${encodeURIComponent(id)}/notifications`, 'GET', null, user, roleHeader(role));

/**
 * GET /pm/orders?<params>
 * Auftragsliste (Meine Auftraege / Auftragsbestand / Freigaben), gefiltert.
 * @param {Object} user
 * @param {Object} [params] — beliebige Filter (z. B. { status, scope, q }),
 *                            werden via URLSearchParams serialisiert.
 */
export const listOrders = async (user, params = {}, role) => {
  const qs = new URLSearchParams(params);
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return pmCall(`/pm/orders${query}`, 'GET', null, user, roleHeader(role));
};

/**
 * GET /pm/orders/:orderId
 * Auftragsdetail (Arbeitsgaenge, Komponenten, Status).
 * @param {Object} user
 * @param {string} orderId
 */
export const getOrder = async (user, orderId, role) =>
  pmCall(`/pm/orders/${encodeURIComponent(orderId)}`, 'GET', null, user, roleHeader(role));

/**
 * GET /pm/dashboard?plant=
 * Meister-Uebersicht: Kennzahlen + Aufschluesselungen + neueste Meldungen.
 * Jede Kennzahl traegt `source` ('mock'|'sap') — die UI zeigt bei 'mock' das Mock-Badge.
 * @param {Object} user
 * @param {string} plant — aktives Werk (me.activePlant)
 * @param {string} [role]
 */
export const getDashboard = async (user, plant, role) =>
  pmCall(`/pm/dashboard?plant=${encodeURIComponent(plant)}`, 'GET', null, user, roleHeader(role));

/**
 * GET /pm/materials?q=
 * Materialsuche.
 * @param {Object} user
 * @param {string} q — Suchbegriff
 */
export const searchMaterials = async (user, q, role) =>
  pmCall(`/pm/materials?q=${encodeURIComponent(q)}`, 'GET', null, user, roleHeader(role));

/**
 * GET /pm/materials/:id/stock?plant=
 * Bestand eines Materials je Werk.
 * @param {Object} user
 * @param {string} id — Materialnummer
 * @param {string} plant — Werk
 */
export const materialStock = async (user, id, plant, role) =>
  pmCall(`/pm/materials/${encodeURIComponent(id)}/stock?plant=${encodeURIComponent(plant)}`, 'GET', null, user, roleHeader(role));

/**
 * POST /pm/orders  (Header: Idempotency-Key, X-PM-Role)
 * Legt einen Instandhaltungsauftrag in SAP an (ueber den Adapter).
 * Ein Retry mit gleichem Key liefert dasselbe Ergebnis — kein Duplikat.
 * @param {Object} user
 * @param {Object} body — Auftrags-Payload
 * @param {string} idempotencyKey
 * @param {string} [role] — aktive PM-Rolle (me.profile.id) fuer das Server-Gate
 */
export const createOrder = async (user, body, idempotencyKey, role) =>
  pmCall('/pm/orders', 'POST', body, user, writeHeaders(idempotencyKey, role));

/**
 * POST /pm/notifications  (Header: Idempotency-Key, X-PM-Role)
 * Legt eine Meldung in SAP an.
 * @param {Object} user
 * @param {Object} body — Meldungs-Payload
 * @param {string} idempotencyKey
 * @param {string} [role]
 */
export const createNotification = async (user, body, idempotencyKey, role) =>
  pmCall('/pm/notifications', 'POST', body, user, writeHeaders(idempotencyKey, role));

/**
 * POST /pm/orders/:orderId/components  (Header: Idempotency-Key, X-PM-Role)
 * Uebergibt Komponenten an einen Auftrag. Reservierung/BANF erzeugt SAP selbst —
 * die Ergebnisse kommen in result.messages und als Component-Felder am Auftrag zurueck.
 * @param {Object} user
 * @param {string} orderId
 * @param {Array<{materialId: string, requiredQty: number, unit?: string}>} components
 * @param {string} idempotencyKey
 * @param {string} [role]
 */
export const addOrderComponents = async (user, orderId, components, idempotencyKey, role) =>
  pmCall(`/pm/orders/${encodeURIComponent(orderId)}/components`, 'POST', { components }, user, writeHeaders(idempotencyKey, role));

/**
 * POST /pm/orders/:orderId/confirmations  (Header: Idempotency-Key, X-PM-Role)
 * Bucht eine Rueckmeldung (Zeit/Mengen) zu einem Arbeitsgang.
 * @param {Object} user
 * @param {string} orderId
 * @param {Object} body — Rueckmelde-Payload
 * @param {string} idempotencyKey
 * @param {string} [role]
 */
export const confirmOperation = async (user, orderId, body, idempotencyKey, role) =>
  pmCall(`/pm/orders/${encodeURIComponent(orderId)}/confirmations`, 'POST', body, user, writeHeaders(idempotencyKey, role));

/**
 * POST /pm/orders/:orderId/release  (Header: Idempotency-Key, X-PM-Role, kein Body)
 * Gibt einen Auftrag frei.
 * @param {Object} user
 * @param {string} orderId
 * @param {string} idempotencyKey
 * @param {string} [role]
 */
export const releaseOrder = async (user, orderId, idempotencyKey, role) =>
  pmCall(`/pm/orders/${encodeURIComponent(orderId)}/release`, 'POST', null, user, writeHeaders(idempotencyKey, role));

/**
 * POST /pm/voice/transcribe?lang=  (Body: rohes Audio-Blob, z. B. audio/webm)
 * Diktat ueber den unternehmenseigenen Sprach-Server (Backend-Proxy; nur wenn der
 * Verbindungstest bestanden ist). Eigener fetch statt pmCall: binaerer Body statt JSON.
 * @param {Object} user
 * @param {Blob} audioBlob
 * @param {string} lang — 'de' | 'en'
 * @param {string} [role]
 */
export const transcribeAudio = async (user, audioBlob, lang, role) => {
  const headers = { 'Content-Type': audioBlob.type || 'audio/webm', ...roleHeader(role) };
  if (user?.token && !user.token.startsWith('mock-')) {
    headers['Authorization'] = `Bearer ${user.token}`;
  }
  const response = await fetch(`/api/pm/voice/transcribe?lang=${encodeURIComponent(lang || 'de')}`, {
    method: 'POST', headers, body: audioBlob,
  });
  if (!response.ok) {
    let body = null;
    try { body = await response.json(); } catch { /* kein JSON */ }
    const err = new Error(body?.error || `HTTP error! status: ${response.status}`);
    err.status = response.status;
    throw err;
  }
  return response.json();
};

/**
 * GET/PUT /user-preferences/:key — generische, userbezogene Persistenz (Core-Route, UX-3).
 * PM nutzt sie fuer die Zuletzt-verwendet-Listen (recency.js). Key-Muster: [a-z0-9_-]{1,60}.
 * @param {Object} user
 * @param {string} key
 */
export const getPreference = async (user, key) =>
  pmCall(`/user-preferences/${encodeURIComponent(key)}`, 'GET', null, user);

/** @param {Object} user @param {string} key @param {any} value */
export const setPreference = async (user, key, value) =>
  pmCall(`/user-preferences/${encodeURIComponent(key)}`, 'PUT', { value }, user);

/**
 * POST /pm/admin/reset  (nur Mock-Adapter; Header: X-PM-Role)
 * Demo-Only: setzt angelegte/persistierte Daten auf den Fixture-Ausgangsstand zurueck.
 * Serverseitig auf canManageUsers gegatet (Meister-Profil) — daher Rolle mitschicken.
 * @param {Object} user
 * @param {string} [role]
 */
export const resetData = async (user, role) =>
  pmCall('/pm/admin/reset', 'POST', null, user, roleHeader(role));

// Gebuendelter Default-Export, damit Screens wahlweise `import { listOrders }`
// ODER `import api from '.../api'` (-> api.listOrders) nutzen koennen.
const api = {
  me,
  searchEquipment,
  equipmentNotifications,
  getDashboard,
  listOrders,
  getOrder,
  searchMaterials,
  materialStock,
  createOrder,
  createNotification,
  addOrderComponents,
  confirmOperation,
  releaseOrder,
  resetData,
};

export default api;
